import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { TotalLoadWidget } from "@/components/TotalLoadWidget";
import {
  TotalLoadChart,
  type TotalLoadServiceSeries,
} from "@/components/TotalLoadChart";

// TotalLoadSection — the dashboard's total-show-load block. Owns the shared
// time range, runs the single /api/dyno/total-load fetch, and feeds both the
// headline widget and the overlay chart so they always agree.

const RANGES = [
  { label: "15m", seconds: 900 },
  { label: "1h", seconds: 3600 },
  { label: "4h", seconds: 14400 },
  { label: "24h", seconds: 86400 },
] as const;

interface TotalLoadResponse {
  window_id: string;
  seconds: number;
  step: number;
  services: TotalLoadServiceSeries[];
}

/**
 * Peak total load over the fetched history. apparentVa is the largest
 * per-timestamp sum of every service's apparent power; amps is the largest
 * per-timestamp max-leg of the per-leg service sums. Both null when there is
 * no data. The chart's total-line peak is the same quantity — keeping U5's
 * widget/chart consistency invariant true.
 */
export function computeRangeMax(services: TotalLoadServiceSeries[]): {
  apparentVa: number | null;
  amps: number | null;
} {
  const tsSet = new Set<number>();
  for (const s of services) for (const t of s.ts) tsSet.add(t);
  const allTs = [...tsSet];
  if (allTs.length === 0) return { apparentVa: null, amps: null };

  // Per-service ts -> value lookups.
  const lookups = services.map((s) => {
    const ap = new Map<number, number>();
    const legs: Map<number, number>[] = [new Map(), new Map(), new Map()];
    s.ts.forEach((t, i) => {
      const v = s.apparent_power[i];
      if (v != null) ap.set(t, v);
      const ls = [s.current_l1[i], s.current_l2[i], s.current_l3[i]];
      ls.forEach((lv, leg) => {
        if (lv != null) legs[leg].set(t, lv);
      });
    });
    return { ap, legs };
  });

  let maxVa = -Infinity;
  let maxAmps = -Infinity;
  for (const t of allTs) {
    let vaTotal = 0;
    let vaSeen = false;
    const legTotals = [0, 0, 0];
    let legSeen = false;
    for (const lk of lookups) {
      const v = lk.ap.get(t);
      if (v != null) {
        vaTotal += v;
        vaSeen = true;
      }
      for (let leg = 0; leg < 3; leg++) {
        const lv = lk.legs[leg].get(t);
        if (lv != null) {
          legTotals[leg] += lv;
          legSeen = true;
        }
      }
    }
    if (vaSeen) maxVa = Math.max(maxVa, vaTotal);
    if (legSeen) maxAmps = Math.max(maxAmps, ...legTotals);
  }
  return {
    apparentVa: Number.isFinite(maxVa) ? maxVa : null,
    amps: Number.isFinite(maxAmps) ? maxAmps : null,
  };
}

interface HistoryState {
  loading: boolean;
  error: boolean;
  services: TotalLoadServiceSeries[];
  /** Wall-clock seconds at fetch time — anchors the chart x-axis range. */
  endSec: number;
}

/**
 * Fetch /api/dyno/total-load for a window + range. Refetches on range or
 * window change and every 60s so the chart tracks the live show. A
 * monotonic request id drops responses from superseded fetches.
 */
function useTotalLoadHistory(
  windowId: string | null,
  seconds: number,
): HistoryState {
  const [state, setState] = useState<HistoryState>({
    loading: false,
    error: false,
    services: [],
    endSec: Math.floor(Date.now() / 1000),
  });
  const reqId = useRef(0);

  useEffect(() => {
    if (!windowId) {
      setState({
        loading: false,
        error: false,
        services: [],
        endSec: Math.floor(Date.now() / 1000),
      });
      return;
    }

    // windowId is non-null past the guard above; bind a narrowed const so the
    // nested load() closure keeps the narrowed type.
    const wid = windowId;
    let cancelled = false;
    function load() {
      const id = ++reqId.current;
      const endSec = Math.floor(Date.now() / 1000);
      setState((s) => ({ ...s, loading: true, error: false }));
      api
        .get<TotalLoadResponse>(
          `/api/dyno/total-load?window_id=${encodeURIComponent(wid)}&seconds=${seconds}`,
        )
        .then((data) => {
          if (cancelled || id !== reqId.current) return;
          setState({
            loading: false,
            error: false,
            services: data.services ?? [],
            endSec,
          });
        })
        .catch(() => {
          if (cancelled || id !== reqId.current) return;
          setState({ loading: false, error: true, services: [], endSec });
        });
    }

    load();
    const timer = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [windowId, seconds]);

  return state;
}

interface TotalLoadSectionProps {
  windowId: string | null;
  /** The selected event's membership system_ids — scopes the realtime "Now"
   * headline. The history fetch below is scoped server-side by windowId. */
  eventSystemIds: ReadonlySet<string>;
}

export function TotalLoadSection({
  windowId,
  eventSystemIds,
}: TotalLoadSectionProps) {
  const [rangeIdx, setRangeIdx] = useState(1); // default 1h
  const range = RANGES[rangeIdx];
  const history = useTotalLoadHistory(windowId, range.seconds);

  const rangeMax = useMemo(
    () => computeRangeMax(history.services),
    [history.services],
  );

  if (!windowId) return null;

  const xRange: [number, number] = [
    history.endSec - range.seconds,
    history.endSec,
  ];
  // Loading: hold the max back (widget shows the loading treatment) until
  // the first response lands. Error is surfaced separately.
  const maxReady = !history.loading && !history.error;

  return (
    <section>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {/* Widget: fixed-width on desktop so the chart takes the remainder.
            Falls back to full-width on smaller screens. */}
        <div className="lg:w-[320px] lg:shrink-0">
          <TotalLoadWidget
            eventSystemIds={eventSystemIds}
            rangeMaxApparentVa={maxReady ? rangeMax.apparentVa : null}
            rangeMaxAmps={maxReady ? rangeMax.amps : null}
            rangeLabel={range.label}
            historyError={history.error}
          />
        </div>
        {/* Chart: min-w-0 lets it shrink below its content width inside flex.
            The time-range buttons (15m/1h/4h/24h) ride inline with the chart's
            own controls — no longer a standalone header row that wastes space. */}
        <div className="min-w-0 flex-1">
          <TotalLoadChart
            services={history.services}
            range={xRange}
            error={history.error}
            timeRanges={RANGES}
            timeRangeIdx={rangeIdx}
            onTimeRangeChange={setRangeIdx}
          />
        </div>
      </div>
    </section>
  );
}
